import { useCallback, useEffect, useMemo, useState } from "react"
import { ArrowLeft, BarChart3, ChevronDown, ChevronRight, Download, FileText, Loader2, Play, Plus, RotateCcw, Search, Trash2 } from "lucide-react"
import { toast } from "sonner"

import {
  exportReportPdf,
  getReportTemplate,
  runReport,
  searchReportTemplates,
  type ExportReportPdfRequest,
  type ReportRunFilterInput,
  type ReportTemplateDetailDto,
  type ReportTemplateDto,
} from "../api/reportTemplateService"
import { Badge } from "shared/ui/badge"
import { Button } from "shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Checkbox } from "shared/ui/checkbox"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"
import { DEFAULT_TENANT_ID } from "shared/utils/tenantContext"

interface DynamicReportRunPreviewProps {
  reportTemplateId?: string | null
  tenantId?: string | null
  onBack?: () => void
}

type FilterValueState = {
  value: string
  valueTo: string
}

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"]
const NO_SORT_VALUE = "__none__"
const RUNTIME_STATE_KEY = "permitfy_dynamic_report_runtime_state_v1"
const MAX_SORT_RULES = 3
const PDF_THAI_FONT_NAME = "NotoSansThai"
const PDF_THAI_FONT_REGULAR = "NotoSansThai-Regular.ttf"
const PDF_THAI_FONT_BOLD = "NotoSansThai-Bold.ttf"

interface RuntimeStateSnapshot {
  selectedColumns: string[]
  filterValues: Record<string, FilterValueState>
  pageSize: number
  sortRules: SortRuleState[]
  groupByField: string
  sortField?: string
  sortDirection?: "asc" | "desc"
}

type SortRuleState = {
  fieldKey: string
  direction: "asc" | "desc"
}

const parseDefaultFilterValue = (raw?: string | null): FilterValueState => {
  if (!raw) return { value: "", valueTo: "" }

  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const from = parsed.from ?? parsed.start ?? parsed.value ?? ""
      const to = parsed.to ?? parsed.end ?? ""
      return {
        value: from == null ? "" : String(from),
        valueTo: to == null ? "" : String(to),
      }
    }
    if (Array.isArray(parsed)) {
      return { value: parsed.join(","), valueTo: "" }
    }
    return { value: String(parsed), valueTo: "" }
  } catch {
    return { value: raw, valueTo: "" }
  }
}

const prettyValue = (value: unknown) => {
  if (value == null) return "-"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "object") return JSON.stringify(value)
  const text = String(value)
  return text.trim() ? text : "-"
}

const toFilterPayload = (
  key: string,
  operator: string,
  filterValue: FilterValueState,
): ReportRunFilterInput | null => {
  const value = filterValue.value.trim()
  const valueTo = filterValue.valueTo.trim()

  if (!value && !valueTo) return null

  if (operator.toLowerCase() === "between") {
    return {
      fieldKey: key,
      operator,
      value: value || null,
      valueTo: valueTo || null,
    }
  }

  if (operator.toLowerCase() === "in") {
    const list = value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
    if (list.length === 0) return null
    return {
      fieldKey: key,
      operator,
      value: list,
    }
  }

  return {
    fieldKey: key,
    operator,
    value,
  }
}

const toCsvCell = (value: unknown) => {
  if (value == null) return ""
  const text = String(value)
  if (text.includes(",") || text.includes('"') || text.includes("\n") || text.includes("\r")) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

const downloadTextFile = (content: string, fileName: string, mimeType = "text/csv;charset=utf-8") => {
  const blob = new Blob([content], { type: mimeType })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

const downloadBlobFile = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

let thaiFontBinaryRegular: string | null = null
let thaiFontBinaryBold: string | null = null

const arrayBufferToBinaryString = (arrayBuffer: ArrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer)
  const chunkSize = 0x8000
  let binary = ""

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    binary += String.fromCharCode.apply(null, Array.from(chunk))
  }
  return binary
}

const fetchFontBinary = async (fontPath: string) => {
  const response = await fetch(fontPath)
  if (!response.ok) {
    throw new Error(`Failed to load font: ${fontPath}`)
  }
  const buffer = await response.arrayBuffer()
  return arrayBufferToBinaryString(buffer)
}

const ensureThaiFontForPdf = async (doc: any) => {
  try {
    if (!thaiFontBinaryRegular) {
      thaiFontBinaryRegular = await fetchFontBinary("/fonts/NotoSansThai-Regular.ttf")
    }
    if (!thaiFontBinaryBold) {
      thaiFontBinaryBold = await fetchFontBinary("/fonts/NotoSansThai-Bold.ttf")
    }

    const fontList = typeof doc.getFontList === "function" ? doc.getFontList() : {}
    const hasThaiFont = fontList?.[PDF_THAI_FONT_NAME]

    if (!hasThaiFont) {
      doc.addFileToVFS(PDF_THAI_FONT_REGULAR, thaiFontBinaryRegular)
      doc.addFont(PDF_THAI_FONT_REGULAR, PDF_THAI_FONT_NAME, "normal")
      doc.addFileToVFS(PDF_THAI_FONT_BOLD, thaiFontBinaryBold)
      doc.addFont(PDF_THAI_FONT_BOLD, PDF_THAI_FONT_NAME, "bold")
    }

    doc.setFont(PDF_THAI_FONT_NAME, "normal")
    return true
  } catch (error) {
    console.warn("Thai font load failed for PDF fallback.", error)
    return false
  }
}

const readRuntimeSnapshot = (templateId: string): RuntimeStateSnapshot | null => {
  if (!templateId || typeof window === "undefined") return null
  const raw = window.localStorage.getItem(RUNTIME_STATE_KEY)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as Record<string, RuntimeStateSnapshot>
    const snapshot = parsed?.[templateId]
    if (!snapshot || typeof snapshot !== "object") return null
    return {
      selectedColumns: Array.isArray(snapshot.selectedColumns) ? snapshot.selectedColumns : [],
      filterValues: snapshot.filterValues && typeof snapshot.filterValues === "object" ? snapshot.filterValues : {},
      pageSize: Number(snapshot.pageSize) > 0 ? Number(snapshot.pageSize) : 20,
      sortRules: Array.isArray(snapshot.sortRules)
        ? snapshot.sortRules
          .filter((item) => item && typeof item === "object")
          .map((item) => ({
            fieldKey: String(item.fieldKey || ""),
            direction: item.direction === "desc" ? "desc" : "asc",
          }))
        : [],
      groupByField: String(snapshot.groupByField || ""),
      sortField: String(snapshot.sortField || ""),
      sortDirection: snapshot.sortDirection === "desc" ? "desc" : "asc",
    }
  } catch {
    return null
  }
}

const writeRuntimeSnapshot = (templateId: string, snapshot: RuntimeStateSnapshot) => {
  if (!templateId || typeof window === "undefined") return
  let map: Record<string, RuntimeStateSnapshot> = {}

  try {
    const raw = window.localStorage.getItem(RUNTIME_STATE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, RuntimeStateSnapshot>
      if (parsed && typeof parsed === "object") {
        map = parsed
      }
    }
  } catch {
    map = {}
  }

  map[templateId] = snapshot
  window.localStorage.setItem(RUNTIME_STATE_KEY, JSON.stringify(map))
}

export default function DynamicReportRunPreview({ reportTemplateId, tenantId, onBack }: DynamicReportRunPreviewProps) {
  const [templates, setTemplates] = useState<ReportTemplateDto[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState(reportTemplateId ?? "")
  const [selectedTenantId, setSelectedTenantId] = useState(tenantId || DEFAULT_TENANT_ID)
  const [templateDetail, setTemplateDetail] = useState<ReportTemplateDetailDto | null>(null)
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])
  const [filterValues, setFilterValues] = useState<Record<string, FilterValueState>>({})
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([])
  const [resultSource, setResultSource] = useState<"api" | "fallback" | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [elapsedMs, setElapsedMs] = useState<number | undefined>(undefined)
  const [sortRules, setSortRules] = useState<SortRuleState[]>([])
  const [groupByField, setGroupByField] = useState("")
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [isExportingCsv, setIsExportingCsv] = useState(false)
  const [isExportingXlsx, setIsExportingXlsx] = useState(false)
  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setSelectedTemplateId(reportTemplateId ?? "")
  }, [reportTemplateId])

  useEffect(() => {
    setSelectedTenantId(tenantId || DEFAULT_TENANT_ID)
  }, [tenantId])

  const visibleColumns = useMemo(() => {
    if (!templateDetail) return []
    const fields = templateDetail.fields ?? []
    if (!selectedColumns.length) return fields.filter((item) => item.defaultVisible)
    return fields.filter((item) => selectedColumns.includes(item.fieldKey))
  }, [selectedColumns, templateDetail])

  const sortableFieldOptions = useMemo(() => {
    if (!templateDetail) return []
    return (templateDetail.fields ?? []).filter((field) => field.isSortable)
  }, [templateDetail])

  const groupableFieldOptions = useMemo(() => {
    if (!templateDetail) return []
    return (templateDetail.fields ?? []).filter((field) => field.isGroupable || field.isSortable)
  }, [templateDetail])

  const activeSortRules = useMemo(
    () => sortRules.filter((item) => String(item.fieldKey || "").trim()),
    [sortRules],
  )

  const groupedRows = useMemo(() => {
    if (!groupByField) return null

    const groups: Array<{ key: string; rows: Array<{ row: Record<string, unknown>; index: number }> }> = []
    rows.forEach((row, index) => {
      const key = String(prettyValue(row[groupByField]))
      const lastGroup = groups[groups.length - 1]
      if (!lastGroup || lastGroup.key !== key) {
        groups.push({ key, rows: [{ row, index }] })
        return
      }
      lastGroup.rows.push({ row, index })
    })
    return groups
  }, [groupByField, rows])

  const loadTemplates = useCallback(async () => {
    setIsLoadingTemplates(true)
    try {
      const result = await searchReportTemplates({
        tenantId: selectedTenantId,
        status: "active",
        page: 1,
        pageSize: 200,
      })
      setTemplates(result.items ?? [])

      if (!selectedTemplateId) {
        const first = result.items?.[0]
        if (first?.reportTemplateId) {
          setSelectedTemplateId(first.reportTemplateId)
        }
      }
    } catch (error) {
      console.error(error)
      toast.error("Failed to load report templates")
    } finally {
      setIsLoadingTemplates(false)
    }
  }, [selectedTemplateId, selectedTenantId])

  useEffect(() => {
    loadTemplates().catch(() => undefined)
  }, [loadTemplates])

  const loadTemplateDetail = useCallback(async () => {
    if (!selectedTemplateId) {
      setTemplateDetail(null)
      setSelectedColumns([])
      setFilterValues({})
      setSortRules([])
      setGroupByField("")
      setCollapsedGroups({})
      setRows([])
      return
    }

    setIsLoadingTemplate(true)
    try {
      const detail = await getReportTemplate(selectedTemplateId, selectedTenantId || DEFAULT_TENANT_ID)
      setTemplateDetail(detail)
      const snapshot = readRuntimeSnapshot(detail.reportTemplateId)

      const defaultColumns =
        (detail.fields ?? [])
          .filter((item) => item.defaultVisible)
          .map((item) => item.fieldKey)
      const allColumns = (detail.fields ?? []).map((item) => item.fieldKey)
      const snapshotColumns = (snapshot?.selectedColumns ?? []).filter((fieldKey) => allColumns.includes(fieldKey))
      setSelectedColumns(
        snapshotColumns.length > 0
          ? snapshotColumns
          : defaultColumns.length > 0
            ? defaultColumns
            : allColumns,
      )

      const nextFilterValues: Record<string, FilterValueState> = {}
      ;(detail.filters ?? []).forEach((item) => {
        nextFilterValues[item.fieldKey] = parseDefaultFilterValue(item.defaultValueJson)
      })
      const mergedFilterValues: Record<string, FilterValueState> = { ...nextFilterValues }
      if (snapshot?.filterValues) {
        Object.keys(snapshot.filterValues).forEach((fieldKey) => {
          if (!mergedFilterValues[fieldKey]) return
          mergedFilterValues[fieldKey] = {
            value: snapshot.filterValues[fieldKey]?.value ?? mergedFilterValues[fieldKey].value,
            valueTo: snapshot.filterValues[fieldKey]?.valueTo ?? mergedFilterValues[fieldKey].valueTo,
          }
        })
      }
      setFilterValues(mergedFilterValues)

      const sortableKeys = (detail.fields ?? []).filter((field) => field.isSortable).map((field) => field.fieldKey)
      const snapshotSortRules = (snapshot?.sortRules ?? [])
        .filter((item) => sortableKeys.includes(item.fieldKey))
        .slice(0, MAX_SORT_RULES)
      const legacySortRule =
        snapshotSortRules.length === 0 &&
        snapshot?.sortField &&
        sortableKeys.includes(snapshot.sortField)
          ? [{
              fieldKey: snapshot.sortField,
              direction: snapshot?.sortDirection === "desc" ? "desc" as const : "asc" as const,
            }]
          : []
      setSortRules(snapshotSortRules.length > 0 ? snapshotSortRules : legacySortRule)
      const groupableKeys = (detail.fields ?? [])
        .filter((field) => field.isGroupable || field.isSortable)
        .map((field) => field.fieldKey)
      const nextGroupBy = snapshot?.groupByField && groupableKeys.includes(snapshot.groupByField)
        ? snapshot.groupByField
        : ""
      setGroupByField(nextGroupBy)

      const nextPageSize = Number(snapshot?.pageSize)
      setPageSize(nextPageSize > 0 ? nextPageSize : 20)

      setRows([])
      setTotalItems(0)
      setTotalPages(1)
      setPageNumber(1)
      setResultSource(null)
      setElapsedMs(undefined)
      setCollapsedGroups({})
    } catch (error) {
      console.error(error)
      toast.error("Failed to load template detail")
    } finally {
      setIsLoadingTemplate(false)
    }
  }, [selectedTemplateId, selectedTenantId])

  useEffect(() => {
    loadTemplateDetail().catch(() => undefined)
  }, [loadTemplateDetail])

  useEffect(() => {
    if (!templateDetail?.reportTemplateId) return

    writeRuntimeSnapshot(templateDetail.reportTemplateId, {
      selectedColumns,
      filterValues,
      pageSize,
      sortRules,
      groupByField,
      sortField: sortRules[0]?.fieldKey || "",
      sortDirection: sortRules[0]?.direction || "asc",
    })
  }, [filterValues, groupByField, pageSize, selectedColumns, sortRules, templateDetail])

  useEffect(() => {
    setCollapsedGroups({})
  }, [groupByField, selectedTemplateId])

  const handleToggleColumn = (fieldKey: string, checked: boolean) => {
    setSelectedColumns((prev) => {
      if (checked) {
        if (prev.includes(fieldKey)) return prev
        return [...prev, fieldKey]
      }
      return prev.filter((item) => item !== fieldKey)
    })
  }

  const handleFilterValueChange = (fieldKey: string, patch: Partial<FilterValueState>) => {
    setFilterValues((prev) => ({
      ...prev,
      [fieldKey]: {
        value: prev[fieldKey]?.value ?? "",
        valueTo: prev[fieldKey]?.valueTo ?? "",
        ...patch,
      },
    }))
  }

  const updateSortRule = (index: number, patch: Partial<SortRuleState>) => {
    setSortRules((prev) =>
      prev.map((item, itemIndex) => {
        if (itemIndex !== index) return item
        return {
          ...item,
          ...patch,
        }
      }),
    )
  }

  const addSortRule = () => {
    if (sortRules.length >= MAX_SORT_RULES) return
    const used = new Set(sortRules.map((item) => item.fieldKey))
    const nextField = sortableFieldOptions.find((item) => !used.has(item.fieldKey))
    if (!nextField) return
    setSortRules((prev) => [...prev, { fieldKey: nextField.fieldKey, direction: "asc" }])
  }

  const removeSortRule = (index: number) => {
    setSortRules((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
  }

  const executeRun = useCallback(
    async (nextPage = 1) => {
      if (!templateDetail) {
        toast.error("Please select report template")
        return
      }
      if (selectedColumns.length === 0) {
        toast.error("Please select at least one column")
        return
      }

      const filters = (templateDetail.filters ?? [])
        .map((item) => toFilterPayload(item.fieldKey, item.operator, filterValues[item.fieldKey] ?? { value: "", valueTo: "" }))
        .filter((item): item is ReportRunFilterInput => Boolean(item))
      const sort = buildSortPayload()
      const groupBy = buildGroupByPayload()
      const queryColumns = buildQueryColumns()

      setIsRunning(true)
      try {
        const result = await runReport({
          tenantId: templateDetail.tenantId || DEFAULT_TENANT_ID,
          templateId: templateDetail.reportTemplateId,
          columns: queryColumns,
          filters,
          sort,
          groupBy,
          pageNumber: nextPage,
          pageSize,
        })
        setRows(result.rows ?? [])
        setTotalItems(result.totalItems ?? 0)
        setTotalPages(result.totalPages ?? 1)
        setPageNumber(result.pageNumber ?? nextPage)
        setResultSource(result.source)
        setElapsedMs(result.elapsedMs)
      } catch (error) {
        console.error(error)
        toast.error("Failed to run report")
      } finally {
        setIsRunning(false)
      }
    },
    [filterValues, groupByField, pageSize, selectedColumns, sortRules, templateDetail],
  )

  const handleResetFilters = () => {
    if (!templateDetail) return
    const next: Record<string, FilterValueState> = {}
    ;(templateDetail.filters ?? []).forEach((item) => {
      next[item.fieldKey] = parseDefaultFilterValue(item.defaultValueJson)
    })
    setFilterValues(next)
  }

  const buildRuntimeFilters = () => {
    if (!templateDetail) return []
    return (templateDetail.filters ?? [])
      .map((item) => toFilterPayload(item.fieldKey, item.operator, filterValues[item.fieldKey] ?? { value: "", valueTo: "" }))
      .filter((item): item is ReportRunFilterInput => Boolean(item))
  }

  const buildQueryColumns = () => {
    const fieldKeys = [...selectedColumns]
    sortRules.forEach((rule) => {
      if (rule.fieldKey && !fieldKeys.includes(rule.fieldKey)) {
        fieldKeys.push(rule.fieldKey)
      }
    })
    if (groupByField && !fieldKeys.includes(groupByField)) {
      fieldKeys.push(groupByField)
    }
    return fieldKeys
  }

  const buildSortPayload = () => {
    const sort = activeSortRules
    return sort.length > 0 ? sort : undefined
  }

  const buildGroupByPayload = () => (groupByField ? [groupByField] : undefined)

  const generateFallbackPdf = async (
    templateCode: string,
    templateName: string,
    columnsForExport: Array<{ fieldKey: string; label: string }>,
    exportRows: Array<Record<string, unknown>>,
    groupedBy?: string,
  ) => {
    const [{ jsPDF }, autoTableModule] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
    ])

    const autoTable = autoTableModule.default
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    })
    const hasThaiFont = await ensureThaiFontForPdf(doc)
    const fontName = hasThaiFont ? PDF_THAI_FONT_NAME : "helvetica"

    const head = [columnsForExport.map((column) => column.label)]
    const body: string[][] = []
    const groupedRowIndexes = new Set<number>()

    if (groupedBy) {
      let currentGroup = ""
      let currentCount = 0
      let currentGroupRowIndex = -1

      exportRows.forEach((row, index) => {
        const groupKey = String(prettyValue(row[groupedBy]))
        if (index === 0 || groupKey !== currentGroup) {
          currentGroup = groupKey
          currentCount = 0
          const groupRow = [`${groupKey}`, ...Array(Math.max(0, columnsForExport.length - 1)).fill("")]
          body.push(groupRow)
          currentGroupRowIndex = body.length - 1
          groupedRowIndexes.add(currentGroupRowIndex)
        }

        currentCount += 1
        if (currentGroupRowIndex >= 0) {
          body[currentGroupRowIndex][0] = `${currentGroup} (${currentCount})`
        }

        body.push(columnsForExport.map((column) => String(prettyValue(row[column.fieldKey]))))
      })
    } else {
      exportRows.forEach((row) => {
        body.push(columnsForExport.map((column) => String(prettyValue(row[column.fieldKey]))))
      })
    }

    const reportTitle = templateName || templateCode || "Dynamic Report"
    doc.setFont(fontName, "bold")
    doc.setFontSize(14)
    doc.text(reportTitle, 40, 36)
    doc.setFont(fontName, "normal")
    doc.setFontSize(9)
    doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 52)

    autoTable(doc, {
      head,
      body,
      startY: 64,
      theme: "grid",
      styles: {
        font: fontName,
        fontSize: 8,
        cellPadding: 4,
        overflow: "linebreak",
        textColor: [17, 24, 39],
      },
      headStyles: {
        font: fontName,
        fillColor: [243, 244, 246],
        textColor: [17, 24, 39],
        fontStyle: "bold",
      },
      didParseCell: (hookData) => {
        if (!groupedRowIndexes.has(hookData.row.index)) return

        hookData.cell.styles.fillColor = [239, 246, 255]
        hookData.cell.styles.textColor = [29, 78, 216]
        hookData.cell.styles.fontStyle = "bold"
        if (hookData.column.index > 0) {
          hookData.cell.text = [""]
        }
      },
    })

    const dateStamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")
    const fileName = `${templateCode || "report"}-${dateStamp}.pdf`
    doc.save(fileName)
    return hasThaiFont
  }

  const handleExportCsv = async () => {
    if (!templateDetail) {
      toast.error("Please select report template")
      return
    }
    if (selectedColumns.length === 0) {
      toast.error("Please select at least one column")
      return
    }

    const filters = buildRuntimeFilters()
    const sort = buildSortPayload()
    const groupBy = buildGroupByPayload()
    const queryColumns = buildQueryColumns()
    const columnsForExport = (templateDetail.fields ?? []).filter((item) => selectedColumns.includes(item.fieldKey))

    setIsExportingCsv(true)
    try {
      const result = await runReport({
        tenantId: templateDetail.tenantId || DEFAULT_TENANT_ID,
        templateId: templateDetail.reportTemplateId,
        columns: queryColumns,
        filters,
        sort,
        groupBy,
        pageNumber: 1,
        pageSize: 5000,
      })

      const header = columnsForExport.map((field) => toCsvCell(field.label || field.fieldKey)).join(",")
      const body = (result.rows ?? []).map((row) => (
        columnsForExport.map((field) => toCsvCell(row[field.fieldKey])).join(",")
      ))
      const csv = [header, ...body].join("\n")

      const dateStamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")
      const fileName = `${templateDetail.code || "report"}-${dateStamp}.csv`
      downloadTextFile(csv, fileName)
      toast.success(`CSV exported (${result.rows.length} rows)`)
    } catch (error) {
      console.error(error)
      toast.error("Failed to export CSV")
    } finally {
      setIsExportingCsv(false)
    }
  }

  const handleExportXlsx = async () => {
    if (!templateDetail) {
      toast.error("Please select report template")
      return
    }
    if (selectedColumns.length === 0) {
      toast.error("Please select at least one column")
      return
    }

    const filters = buildRuntimeFilters()
    const sort = buildSortPayload()
    const groupBy = buildGroupByPayload()
    const queryColumns = buildQueryColumns()
    const columnsForExport = (templateDetail.fields ?? []).filter((item) => selectedColumns.includes(item.fieldKey))

    setIsExportingXlsx(true)
    try {
      const result = await runReport({
        tenantId: templateDetail.tenantId || DEFAULT_TENANT_ID,
        templateId: templateDetail.reportTemplateId,
        columns: queryColumns,
        filters,
        sort,
        groupBy,
        pageNumber: 1,
        pageSize: 5000,
      })

      const xlsx = await import("xlsx")
      const exportRows = (result.rows ?? []).map((row) => {
        const mapped: Record<string, unknown> = {}
        columnsForExport.forEach((field) => {
          mapped[field.label || field.fieldKey] = row[field.fieldKey]
        })
        return mapped
      })

      const worksheet = xlsx.utils.json_to_sheet(exportRows)
      const workbook = xlsx.utils.book_new()
      xlsx.utils.book_append_sheet(workbook, worksheet, "Report")
      const dateStamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")
      const fileName = `${templateDetail.code || "report"}-${dateStamp}.xlsx`
      xlsx.writeFile(workbook, fileName)
      toast.success(`XLSX exported (${result.rows.length} rows)`)
    } catch (error) {
      console.error(error)
      toast.error("Failed to export XLSX")
    } finally {
      setIsExportingXlsx(false)
    }
  }

  const handleExportPdf = async () => {
    if (!templateDetail) {
      toast.error("Please select report template")
      return
    }
    if (selectedColumns.length === 0) {
      toast.error("Please select at least one column")
      return
    }

    const filters = buildRuntimeFilters()
    const sort = buildSortPayload()
    const groupBy = buildGroupByPayload()
    const queryColumns = buildQueryColumns()
    const columnsForExport = (templateDetail.fields ?? [])
      .filter((item) => selectedColumns.includes(item.fieldKey))
      .map((item) => ({
        fieldKey: item.fieldKey,
        label: item.label || item.fieldKey,
      }))

    setIsExportingPdf(true)
    try {
      const requestPayload: ExportReportPdfRequest = {
        tenantId: templateDetail.tenantId || DEFAULT_TENANT_ID,
        templateId: templateDetail.reportTemplateId,
        columns: queryColumns,
        filters,
        sort,
        groupBy,
      }

      const pdfBlob = await exportReportPdf(requestPayload)
      if (pdfBlob && pdfBlob.size > 0) {
        const dateStamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")
        const fileName = `${templateDetail.code || "report"}-${dateStamp}.pdf`
        downloadBlobFile(pdfBlob, fileName)
        toast.success("PDF exported")
        return
      }

      const result = await runReport({
        ...requestPayload,
        pageNumber: 1,
        pageSize: 5000,
      })

      const usedThaiFont = await generateFallbackPdf(
        templateDetail.code,
        templateDetail.name,
        columnsForExport,
        result.rows ?? [],
        groupByField || undefined,
      )
      if (usedThaiFont) {
        toast.info("Using client-side PDF fallback.")
      } else {
        toast.warning("Using client-side PDF fallback without Thai font. Please verify Thai text rendering.")
      }
    } catch (error) {
      console.error(error)
      toast.error("Failed to export PDF")
    } finally {
      setIsExportingPdf(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <BarChart3 className="h-5 w-5 text-purple-600" />
              Dynamic Report Run Preview
            </CardTitle>
            <p className="text-sm text-gray-500">Select template, apply filters, and preview report result</p>
          </div>
          <div className="flex items-center gap-2">
            {onBack ? (
              <Button variant="outline" className="gap-2" onClick={onBack}>
                <ArrowLeft className="h-4 w-4" />
                Back to Template List
              </Button>
            ) : null}
            <Button
              variant="outline"
              onClick={handleExportCsv}
              disabled={
                isExportingCsv || isExportingXlsx || isExportingPdf || isRunning || isLoadingTemplate || !templateDetail || selectedColumns.length === 0
              }
              className="gap-2"
            >
              {isExportingCsv ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={handleExportXlsx}
              disabled={
                isExportingCsv || isExportingXlsx || isExportingPdf || isRunning || isLoadingTemplate || !templateDetail || selectedColumns.length === 0
              }
              className="gap-2"
            >
              {isExportingXlsx ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export XLSX
            </Button>
            <Button
              variant="outline"
              onClick={handleExportPdf}
              disabled={
                isExportingCsv || isExportingXlsx || isExportingPdf || isRunning || isLoadingTemplate || !templateDetail || selectedColumns.length === 0
              }
              className="gap-2"
            >
              {isExportingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              Export PDF
            </Button>
            <Button onClick={() => executeRun(1)} disabled={isRunning || isLoadingTemplate || !templateDetail} className="gap-2">
              {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Run Report
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2 md:col-span-2">
              <Label>Report Template</Label>
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                <SelectTrigger>
                  <SelectValue placeholder={isLoadingTemplates ? "Loading templates..." : "Select report template"} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((item) => (
                    <SelectItem key={item.reportTemplateId} value={item.reportTemplateId}>
                      {item.code} - {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Page Size</Label>
              <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number.parseInt(value, 10) || 20)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={size}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <div className="space-y-2 md:col-span-2">
              <div className="flex items-center justify-between">
                <Label>Sort Rules</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1"
                  onClick={addSortRule}
                  disabled={!templateDetail || sortRules.length >= MAX_SORT_RULES || sortableFieldOptions.length === 0}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Sort
                </Button>
              </div>
              {sortRules.length === 0 ? (
                <div className="rounded-md border border-dashed p-3 text-sm text-gray-500">No sorting</div>
              ) : (
                <div className="space-y-2">
                  {sortRules.map((rule, index) => {
                    const usedByOthers = new Set(
                      sortRules
                        .filter((_, itemIndex) => itemIndex !== index)
                        .map((item) => item.fieldKey),
                    )
                    const selectableFields = sortableFieldOptions.filter((field) => !usedByOthers.has(field.fieldKey))

                    return (
                      <div key={`${rule.fieldKey}-${index}`} className="grid grid-cols-12 gap-2">
                        <div className="col-span-7">
                          <Select
                            value={rule.fieldKey || NO_SORT_VALUE}
                            onValueChange={(value) => updateSortRule(index, { fieldKey: value === NO_SORT_VALUE ? "" : value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select field" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NO_SORT_VALUE}>Select field</SelectItem>
                              {selectableFields.map((field) => (
                                <SelectItem key={field.fieldKey} value={field.fieldKey}>
                                  {field.label || field.fieldKey}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-4">
                          <Select
                            value={rule.direction}
                            onValueChange={(value) => updateSortRule(index, { direction: value === "desc" ? "desc" : "asc" })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="asc">ASC</SelectItem>
                              <SelectItem value="desc">DESC</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeSortRule(index)}
                            aria-label={`remove-sort-${index}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Group By</Label>
              <Select
                value={groupByField || NO_SORT_VALUE}
                onValueChange={(value) => setGroupByField(value === NO_SORT_VALUE ? "" : value)}
                disabled={!templateDetail}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No grouping" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_SORT_VALUE}>No grouping</SelectItem>
                  {groupableFieldOptions.map((field) => (
                    <SelectItem key={field.fieldKey} value={field.fieldKey}>
                      {field.label || field.fieldKey}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {templateDetail ? (
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              <Badge variant="outline">Code: {templateDetail.code}</Badge>
              <Badge variant="outline">Version: v{templateDetail.version}</Badge>
              <Badge variant="outline">Source: {templateDetail.dataSourceType}</Badge>
              <Badge className={templateDetail.isActive ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-700"}>
                {templateDetail.isActive ? "ACTIVE" : "INACTIVE"}
              </Badge>
              {resultSource ? (
                <Badge className={resultSource === "api" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}>
                  Result Source: {resultSource.toUpperCase()}
                </Badge>
              ) : null}
              {elapsedMs != null ? <Badge variant="outline">Elapsed: {elapsedMs} ms</Badge> : null}
              {activeSortRules.length > 0 ? (
                <Badge variant="outline">
                  Sort: {activeSortRules.map((item) => `${item.fieldKey}:${item.direction.toUpperCase()}`).join(", ")}
                </Badge>
              ) : null}
              {groupByField ? <Badge variant="outline">Group By: {groupByField}</Badge> : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {templateDetail ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Runtime Filters</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {templateDetail.filters.length === 0 ? (
              <p className="text-sm text-gray-500">No preconfigured filters in this template.</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {templateDetail.filters.map((filter) => {
                  const state = filterValues[filter.fieldKey] ?? { value: "", valueTo: "" }
                  const isBetween = filter.operator.toLowerCase() === "between"

                  return (
                    <div key={`${filter.fieldKey}-${filter.orderIndex}`} className="space-y-2 rounded-md border p-3">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-sm">{filter.fieldKey}</Label>
                        <Badge variant="outline">{filter.operator}</Badge>
                      </div>
                      {isBetween ? (
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            placeholder="From"
                            value={state.value}
                            onChange={(event) => handleFilterValueChange(filter.fieldKey, { value: event.target.value })}
                          />
                          <Input
                            placeholder="To"
                            value={state.valueTo}
                            onChange={(event) => handleFilterValueChange(filter.fieldKey, { valueTo: event.target.value })}
                          />
                        </div>
                      ) : (
                        <Input
                          placeholder={filter.operator.toLowerCase() === "in" ? "Value1,Value2,Value3" : "Value"}
                          value={state.value}
                          onChange={(event) => handleFilterValueChange(filter.fieldKey, { value: event.target.value })}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={handleResetFilters}>
                <RotateCcw className="h-4 w-4 mr-1" />
                Reset Filters
              </Button>
              <Button onClick={() => executeRun(1)} disabled={isRunning} className="gap-2">
                {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Apply & Run
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {templateDetail ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Columns</CardTitle>
          </CardHeader>
          <CardContent>
            {templateDetail.fields.length === 0 ? (
              <p className="text-sm text-gray-500">No fields configured in this template.</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {templateDetail.fields.map((field) => {
                  const checked = selectedColumns.includes(field.fieldKey)
                  return (
                    <label key={field.fieldKey} className="flex items-center gap-2 rounded border p-2 cursor-pointer">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) => handleToggleColumn(field.fieldKey, Boolean(value))}
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{field.label || field.fieldKey}</div>
                        <div className="text-xs text-gray-500 truncate">{field.fieldKey}</div>
                      </div>
                    </label>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <CardTitle className="text-base">Preview Result</CardTitle>
          <div className="text-sm text-gray-500">
            Showing {totalItems === 0 ? 0 : (pageNumber - 1) * pageSize + 1} - {Math.min(pageNumber * pageSize, totalItems)} of {totalItems}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleColumns.map((field) => (
                    <TableHead
                      key={field.fieldKey}
                      className="whitespace-normal break-words [overflow-wrap:anywhere] align-top"
                    >
                      {field.label || field.fieldKey}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isRunning ? (
                  <TableRow>
                    <TableCell colSpan={Math.max(1, visibleColumns.length)} className="h-24 text-center text-gray-500">
                      <div className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Running report...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={Math.max(1, visibleColumns.length)} className="h-24 text-center text-gray-500">
                      No data
                    </TableCell>
                  </TableRow>
                ) : groupedRows ? (
                  groupedRows.flatMap((group, groupIndex) => {
                    const isCollapsed = Boolean(collapsedGroups[group.key])
                    const headerRow = (
                      <TableRow
                        key={`group-${groupIndex}-${group.key}`}
                        className="bg-blue-50/60 cursor-pointer hover:bg-blue-100/60"
                        onClick={() =>
                          setCollapsedGroups((prev) => ({
                            ...prev,
                            [group.key]: !prev[group.key],
                          }))
                        }
                      >
                        <TableCell colSpan={Math.max(1, visibleColumns.length)} className="text-xs font-medium text-blue-800">
                          <div className="inline-flex items-center gap-2">
                            {isCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            <span>{group.key} ({group.rows.length})</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )

                    if (isCollapsed) {
                      return [headerRow]
                    }

                    return [
                      headerRow,
                      ...group.rows.map((item) => {
                        const row = item.row
                        const index = item.index
                        return (
                          <TableRow key={`${String(row.__permitId || index)}-${index}`}>
                            {visibleColumns.map((field) => (
                              <TableCell
                                key={`${String(row.__permitId || index)}-${field.fieldKey}`}
                                className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] align-top"
                              >
                                {prettyValue(row[field.fieldKey])}
                              </TableCell>
                            ))}
                          </TableRow>
                        )
                      }),
                    ]
                  })
                ) : (
                  rows.map((row, index) => (
                    <TableRow key={`${String(row.__permitId || index)}-${index}`}>
                      {visibleColumns.map((field) => (
                        <TableCell
                          key={`${String(row.__permitId || index)}-${field.fieldKey}`}
                          className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] align-top"
                        >
                          {prettyValue(row[field.fieldKey])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={pageNumber <= 1 || isRunning}
              onClick={() => executeRun(pageNumber - 1)}
            >
              Prev
            </Button>
            <span className="text-sm text-gray-700">
              Page {pageNumber} / {Math.max(1, totalPages)}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={pageNumber >= Math.max(1, totalPages) || isRunning}
              onClick={() => executeRun(pageNumber + 1)}
            >
              Next
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
